import { getURL } from '@domain/infrastructure'
import Toggles from '@domain/toggles/Toggles'
import { fetchWithRefresh } from "./fetch.utils"
import CustomFetchError from './CustomFetchError'

type servicesTypes = 'products' | 'project' | 'v2' | '' | "cms" | 'masters' | 'crm' | 'documents' | 'emails' | 'erp' | 'esat-wizard' | 'images' | 'orders' | 'prices' | 'projects' | 'support' | 'users' | 'transform'
const restMethods = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const
type restMethod = typeof restMethods[number]

class ApiCaller {
    baseUrl: string = ""
    constructor(baseURL: string) {
        this.baseUrl = baseURL || ""
    }

    static isStatus2XX(input: Response) {
        return input.status >= 200 && input.status <= 299
    }

    static async deprecatedCall(service: servicesTypes, method: restMethod, route: string, payload?: any, fetchParams?: any, subdomain = 'api.', headers?: any) {
        const path = service ? `${service}/${route}` : route
        const url = getURL(path, subdomain)

        switch (method) {
            case 'GET':
                const getResponse = await fetch(url, {
                    method,
                    ...fetchParams,
                    credentials: 'include'
                })
                if (ApiCaller.isStatus2XX(getResponse)) {
                    return getResponse.json()
                }

                throw ({
                    ErrorCode: getResponse.status,
                    ErrorDescription: getResponse.statusText
                })
            case 'POST':
            case 'PUT':
                const postResponse = await fetch(url, {
                    method,
                    headers: headers ?? {
                        'Content-Type': 'application/json',
                    },
                    body: payload,
                    ...fetchParams
                })

                if (ApiCaller.isStatus2XX(postResponse)) {
                    if (postResponse.status === 204) return ''
                    return postResponse.json()
                }

                throw ({
                    ErrorCode: postResponse.status,
                    ErrorDescription: postResponse.statusText
                })
            case 'DELETE':
                const getResponse1 = await fetch(url, {
                    method,
                    ...fetchParams
                })
                if (ApiCaller.isStatus2XX(getResponse1)) {
                    return
                }

                throw ({
                    ErrorCode: getResponse1.status,
                    ErrorDescription: getResponse1.statusText
                })
            default:
                throw `Invalid method "${method}". Valid methods 'GET', 'PUT' or 'POST.`
        }
    }

    static async call(service: servicesTypes, method: restMethod, route: string, payload?: any, fetchParams?: any, subdomain = 'api.', headers?: any) {
        const path = service ? `${service}/${route}` : route
        const url = getURL(path, subdomain)

        if (Toggles.isEnabled("useNewApiCaller")) {
            const newFetchParams = { ...fetchParams }

            if (headers) {
                newFetchParams.headers = headers
            } else {
                const hasHeaders = Boolean(newFetchParams.headers)
                if (!hasHeaders && ['PUT', 'POST'].includes(method)) {
                    newFetchParams.headers = { 'Content-Type': 'application/json' }
                }
            }

            const isCredentialsSet = Boolean(newFetchParams.credentials)
            if (!isCredentialsSet && method == 'GET') newFetchParams.credentials = 'include'

            return ApiCaller.plainCall(url, method, payload, newFetchParams)
        } else {
            return ApiCaller.deprecatedCall(service, method, route, payload, fetchParams, subdomain, headers)
        }
    }

    async plainCall(url: string, method: restMethod, payload?: object, configs?: RequestInit) {
        return ApiCaller.plainCall(this.baseUrl + url, method, payload, configs)
    }

    static async plainCall(url: string | URL, method: restMethod, payload?: object | string, fetchParams?: RequestInit) {
        const stringURL = (url as URL).href || (url as string)

        if (!restMethods.includes(method)) {
            throw `Invalid method "${method}". Valid methods 'GET', 'PUT', 'POST' or 'DELETE'`
        }

        const newFetchParams: RequestInit = {
            method: method,
            ...fetchParams
        }

        if (payload) {
            if (typeof payload === 'object') {
                newFetchParams.body = JSON.stringify(payload);
            } else {
                newFetchParams.body = payload;
            }
        }

        const response = await fetchWithRefresh(stringURL, newFetchParams)
        if (ApiCaller.isStatus2XX(response)) {
            const returnJSON = response.headers.get('Content-Type')?.includes('application/json')
            return returnJSON ? response.json() : response
        }

        const error = {
            ErrorCode: response.status,
            ErrorDescription: response.statusText,
            URL: response.url
        }

        throw new CustomFetchError(error)
    }
}

export default ApiCaller